import axios, { AxiosResponse } from "axios";
import { User } from "../../../utils/database";
import { BACKEND_URL } from "../../../utils/api";

interface AdminUserPayLoad {
  first_name: string;
  last_name: string;
  email: string;
  phonenumber: string;
  password: string;
  country_id: number;
  role: "USER" | "ADMIN" | "MODERATOR";
  birthdate: string;
  send_credentials_email: boolean;
}

export const usersService = {
  async createUser(user: User): Promise<User> {
    try {
      const response: AxiosResponse<User> = await axios.post(`${BACKEND_URL}/user`, user);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur :", error);
      throw error;
    }
  },

  async createUserByAdmin(user: AdminUserPayLoad, token: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await axios.post(`${BACKEND_URL}/user/admin/create`, user, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur :", error);
      throw error;
    }
  },

  async getUserById(id: string | number, token: string): Promise<User | null> {
    try {
      const response: AxiosResponse<User> = await axios.get(`${BACKEND_URL}/user/user_id?q=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'utilisateur ${id} :`, error);
      return null;
    }
  },

  async remindNoKyC(token: string): Promise<any> {
    try {
      const response: AxiosResponse<User> = await axios.post(
        `${BACKEND_URL}/kyc/remind-no-kyc`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de l'envoi de mails au utilisateurs :`, error);
      return null;
    }
  },

  async searchUsers(token: string, query: string): Promise<any[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/user/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.users || [];
    } catch (error) {
      console.error("Erreur lors de la recherche d'utilisateurs :", error);
      return [];
    }
  },

  async getUsers(token: string): Promise<User[]> {
    try {
      const response = await axios.get<{ message: string; users: User[] }>(`${BACKEND_URL}/user/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data.users;
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs :", error);
      throw error;
    }
  },

  async updateUser(id: string | number, updatedData?: Partial<User>, token?: string): Promise<any> {
    try {
      const response: AxiosResponse<User> = await axios.put(`${BACKEND_URL}/user/${id}`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'utilisateur ${id} :`, error);
      throw error;
    }
  },

  async deleteUser(id: string | number): Promise<boolean> {
    try {
      await axios.delete(`${BACKEND_URL}/user/${id}`);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'utilisateur ${id} :`, error);
      throw error;
    }
  },

  // Get user with KYC documents
  async getUserWithKYC(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/kyc/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  // Approve user KYC
  async approveUserKYC(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/kyc/activate-user?user_id=${userId}&is_verified=YES`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de l'approbation du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  // Reject user KYC (old method - kept for compatibility)
  async rejectUserKYC(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/kyc/activate-user?user_id=${userId}&is_verified=NO`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du rejet du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  // Reject user KYC with reason and notification methods
  async rejectUserKYCWithReason(
    userId: string,
    reason: string,
    notificationMethods: string[],
    token: string
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/kyc/reject-kyc`,
        {
          user_id: userId,
          reason: reason,
          notification_methods: notificationMethods,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du rejet du KYC pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  // Update user status
  async updateUserStatus(userId: string, status: "ACTIVE" | "INACTIVE", token: string): Promise<any> {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/user/${userId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut pour l'utilisateur ${userId} :`, error);
      throw error;
    }
  },

  // Get current user by token
  async getCurrentUser(token: string): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/user/get_user_by_token`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.user;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'utilisateur connecté :", error);
      throw error;
    }
  },

  // Get KYC status for a user
  async getUserKYCStatus(userId: string, token: string): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/kyc/check-verified?user_id=${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du statut KYC pour l'utilisateur ${userId} :`, error);
      return { kyc_status: "NO_KYC", has_kyc: false, is_verified: "NO" };
    }
  },

  async changeUserCountry(userId: string, newCountryId: number, token: string): Promise<any> {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/user/${userId}/country`,
        {
          country_id: newCountryId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du statut KYC pour l'utilisateur ${userId} :`, error);
      return { kyc_status: "NO_KYC", has_kyc: false, is_verified: "NO" };
    }
  },

  async getCountryHistory(userId: string, token: string) {
    try {
      const response = await axios.get(`${BACKEND_URL}/user/${userId}/country-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération de historique de pays  pour l'utilisateur 
        ${userId} :`,
        error
      );
      return error;
    }
  },

  async submitUserKYC(userId: string, payload: FormData, token?: string) {
    try {
      const response = await axios.put(`${BACKEND_URL}/kyc/admin/user/${userId} `, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        // ⚠️ Empêche axios de transformer/sérialiser le FormData
        transformRequest: [(data) => data],
      });
      return response.data;
    } catch (error: any) {
      console.error(`Erreur lors de l'upload du KYC pour l'utilisateur ${userId} :`, error.response);
      throw error;
    }
  },
};
